import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Edit,
  Plus,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Calendar,
  Mail,
  Phone,
  MoreVertical,
  ShieldAlert,
  Globe,
  Clock,
  X,
} from 'lucide-react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import PatientPortalSection from '@/components/patient/PatientPortalSection';
import ReconcileAllergiesDialog from '@/components/visit/ReconcileAllergiesDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import PatientDetailSidebar from '@/components/patient/PatientDetailSidebar';
import PatientSecondaryNav from '@/components/patient/PatientSecondaryNav';
import PatientFormDrawer from '@/components/clinic/PatientFormDrawer';
import PrescriptionFormSheet from '@/components/clinic/PrescriptionFormSheet';
import GuidedOrderPickerDialog from '@/components/clinic/GuidedOrderPickerDialog';
import AppointmentWizard from '@/components/emr/AppointmentWizard';
import { toast } from '@/hooks/use-toast';
import NoteFormDrawer from '@/components/clinic/NoteFormDrawer';
import UpdatePatientStatusDialog from '@/components/patient/UpdatePatientStatusDialog';
import PatientSummaryTab from '@/components/patient/PatientSummaryTab';
import PatientProfileTab from '@/components/patient/PatientProfileTab';
import PatientChartTab, {
  ChartAnchor,
} from '@/components/patient/PatientChartTab';
import PatientOrdersRxTab from '@/components/patient/PatientOrdersRxTab';
import PatientNotesTab from '@/components/patient/PatientNotesTab';
import SOAPNotesListSheet from '@/components/patient/SOAPNotesListSheet';
import PatientShipmentsTab from '@/components/patient/PatientShipmentsTab';
import PatientIntakesTab from '@/components/patient/PatientIntakesTab';
import PatientLabsTab from '@/components/patient/PatientLabsTab';
import PatientDocumentsTab from '@/components/patient/PatientDocumentsTab';
import PatientInvoicesTab from '@/components/patient/PatientInvoicesTab';
import PatientAppointmentsTab, {
  patientAppointmentsQueryKey,
} from '@/components/patient/PatientAppointmentsTab';
import PatientMdiCasesTab from '@/components/patient/PatientMdiCasesTab';
import PatientGuidedOrdersTab from '@/components/patient/PatientGuidedOrdersTab';
import PatientMessagingTab from '@/components/patient/PatientMessagingTab';
import PatientTimelineTab from '@/components/patient/PatientTimelineTab';
import { TaskPanel } from '@/components/tasks/TaskPanel';
import { cn, getDefaultContactValue, toTitleCase } from '@/lib/utils';
import { patientService } from '@/services/patientService';
import { mdiService } from '@/services/mdiService';
import { emrService, EmrClinicalNote } from '@/services/emrService';
import { Patient, PaginationParams } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import '@/prescription.css';
import { useClinic } from '@/contexts/ClinicContext';
import { permissionCheck } from '@/lib/permission-check';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbLabel } from '@/components/ui/breadcrumb';
import { formatDOB, calculateAge } from '@/lib/dateUtils';

// Map legacy `?tab=` values from before the 22→5 consolidation onto the new
// destinations. Keeps deep links from emails / bookmarks / tasks working.
const LEGACY_TAB_REDIRECTS: Record<string, string> = {
  overview: 'summary',
  intellihealth: 'summary',
  problems: 'chart',
  allergies: 'chart',
  'medication-list': 'chart',
  immunizations: 'chart',
  vitals: 'chart',
  prescriptions: 'orders-rx',
  medications: 'orders-rx',
  'soap-notes': 'notes',
  // Timeline is no longer a tab — it lives in the patient header ⋮ dropdown
  // and renders as a Sheet. Legacy ?tab=timeline URLs auto-open that Sheet
  // (see effect below) and the underlying tab routes to summary.
  timeline: 'summary',
};

// Legacy chart sub-anchors so links like `?tab=allergies` still scroll to the
// right section once they've been redirected to `chart`.
const LEGACY_TO_ANCHOR: Record<string, ChartAnchor> = {
  problems: 'problems',
  allergies: 'allergies',
  'medication-list': 'medications',
  immunizations: 'immunizations',
  vitals: 'vitals',
};

const PatientDetail = () => {
  const { user } = useAuth();
  const { clinic, isAdmin, isPractitioner, rolePermissions } = useClinic();
  const isGuidedClinic = clinic?.clinic_archetype === 'guided';
  const isHybridClinic = clinic?.clinic_archetype === 'hybrid';
  const showIntakesTab = !isGuidedClinic; // direct + hybrid
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');

  // Resolve legacy tab to its new home (and capture chart anchor if applicable).
  const resolvedTab = tabFromUrl
    ? LEGACY_TAB_REDIRECTS[tabFromUrl] ?? tabFromUrl
    : 'summary';
  const initialChartAnchor: ChartAnchor | undefined =
    tabFromUrl && LEGACY_TO_ANCHOR[tabFromUrl]
      ? LEGACY_TO_ANCHOR[tabFromUrl]
      : undefined;

  // State for modals and sheets
  const [patient, setPatient] = useState<Patient | null>(null);
  const isExternalPatient = !!(
    patient?.clinic_id && clinic?.id && patient.clinic_id !== clinic.id
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isNoteDrawerOpen, setIsNoteDrawerOpen] = useState(false);
  const [isPrescriptionDrawerOpen, setIsPrescriptionDrawerOpen] = useState(false);
  const [isGuidedPickerOpen, setIsGuidedPickerOpen] = useState(false);
  const [pendingGuidedOrderId, setPendingGuidedOrderId] = useState<string | null>(null);
  const [soapNotesSheetOpen, setSoapNotesSheetOpen] = useState(false);
  const [shipmentsSheetOpen, setShipmentsSheetOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(resolvedTab);
  const [chartAnchor, setChartAnchor] = useState<ChartAnchor | undefined>(
    initialChartAnchor,
  );
  const [recentSoapNotes, setRecentSoapNotes] = useState<EmrClinicalNote[]>([]);
  const [isLoadingSoapNotes, setIsLoadingSoapNotes] = useState(true);
  const [recentShipments] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [showReconcileAllergies, setShowReconcileAllergies] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'active' | 'inactive'>('active');
  const [isAppointmentSheetOpen, setIsAppointmentSheetOpen] = useState(false);
  const [isPortalSheetOpen, setIsPortalSheetOpen] = useState(false);
  const [isTimelineSheetOpen, setIsTimelineSheetOpen] = useState(false);
  const [isMdiEnabled, setIsMdiEnabled] = useState(false);
  const { setLabel: setBreadcrumbLabel } = useBreadcrumbLabel();

  const canViewTasksTab = Boolean(user?.practitionerId) && (isPractitioner || isAdmin);

  const [soapNoteParams, setSoapNoteParams] = useState<PaginationParams>({
    page: 1,
    limit: 5,
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });

  // Sync URL → state. Two paths:
  //   - legacy tab values (e.g. ?tab=overview) get rewritten and routed
  //     to their new home;
  //   - current tab values (e.g. ?tab=orders-rx pushed by an inline
  //     "See all" link) need to actually switch `activeTab`, otherwise
  //     the URL changes but the visible tab doesn't.
  useEffect(() => {
    if (!tabFromUrl) return;
    if (LEGACY_TAB_REDIRECTS[tabFromUrl]) {
      const next = LEGACY_TAB_REDIRECTS[tabFromUrl];
      setActiveTab(next);
      if (LEGACY_TO_ANCHOR[tabFromUrl]) {
        setChartAnchor(LEGACY_TO_ANCHOR[tabFromUrl]);
      }
      // Legacy ?tab=timeline → open the Timeline Sheet (timeline lives in
      // the ⋮ menu now, not as a tab).
      if (tabFromUrl === 'timeline') {
        setIsTimelineSheetOpen(true);
      }
      // Rewrite URL silently so subsequent navigation stays clean.
      const next_params = new URLSearchParams(searchParams);
      next_params.set('tab', next);
      setSearchParams(next_params, { replace: true });
    } else if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      if (tabFromUrl !== 'chart') {
        setChartAnchor(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);

  const handleTabChange = (next: string) => {
    setActiveTab(next);
    if (next !== 'chart') {
      setChartAnchor(undefined);
    }
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: false });
  };

  const handleEditPatient = async (data: { success?: boolean }) => {
    if (data?.success && patient?.id) {
      toast({
        title: 'Success',
        description: 'Patient updated successfully',
        variant: 'success',
      });
      await fetchPatient(patient.id);
      setIsEditDrawerOpen(false);
    }
  };

  const fetchSoapNotes = async (patientId: string) => {
    if (!clinic?.id) return;
    setIsLoadingSoapNotes(true);
    try {
      const response = await emrService.getPatientNotesPaginated(clinic.id, patientId, {
        page: soapNoteParams.page,
        limit: soapNoteParams.limit,
      });
      setRecentSoapNotes(response.data);
      setSoapNoteParams(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching clinical notes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load clinical notes',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingSoapNotes(false);
    }
  };

  const handleCreateNote = async () => {
    if (id) {
      await fetchSoapNotes(id);
      handleTabChange('notes');
    }
  };

  const onSuccess = () => {
    handleTabChange('orders-rx');
  };

  const fetchPatient = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await patientService.getPatientById(id);
      setPatient(response.data);
    } catch (error) {
      console.error('Error fetching patient:', error);
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshPatient = async (id: string) => {
    try {
      const response = await patientService.getPatientById(id);
      setPatient(response.data);
    } catch (error) {
      console.error('Error refreshing patient:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchPatient(id);
    }
  }, [id]);

  useEffect(() => {
    setBreadcrumbLabel(patient?.full_name ? toTitleCase(patient.full_name) : null);
    return () => setBreadcrumbLabel(null);
  }, [patient?.full_name, setBreadcrumbLabel]);

  // Redirect to summary if current tab is not available for the clinic archetype
  useEffect(() => {
    if (
      isGuidedClinic &&
      (activeTab === 'appointments' ||
        activeTab === 'notes' ||
        activeTab === 'chart' ||
        activeTab === 'intakes')
    ) {
      handleTabChange('summary');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuidedClinic, activeTab]);

  useEffect(() => {
    if (patient?.id) {
      fetchSoapNotes(patient.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, soapNoteParams.page]);

  // Check if MDI is enabled for the clinic
  useEffect(() => {
    const checkMdiEnabled = async () => {
      if (clinic?.id) {
        try {
          const enabled = await mdiService.isEnabled(clinic.id);
          setIsMdiEnabled(enabled);
        } catch (error) {
          console.error('Error checking MDI status:', error);
          setIsMdiEnabled(false);
        }
      }
    };
    checkMdiEnabled();
  }, [clinic?.id]);

  const getDefaultContact = (key: 'email' | 'phone') => {
    const contacts =
      patient?.contacts?.filter((c: { key?: string }) => c.key === key) || [];
    const defaultContact = contacts.find(
      (c: { is_default?: boolean }) => c.is_default,
    );
    const fallback = contacts[0]?.value;
    const patientField = key === 'email' ? patient?.email : patient?.phone;
    return defaultContact?.value || fallback || patientField || null;
  };

  const defaultEmail = getDefaultContact('email');
  const defaultPhone = getDefaultContact('phone');

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Active',
          className: 'bg-emerald-600 text-white border-0',
          icon: <CheckCircle className='h-3 w-3' />,
        };
      case 'inactive':
        return {
          label: 'Inactive',
          className: 'bg-slate-500 text-white border-0',
          icon: <XCircle className='h-3 w-3' />,
        };
      default:
        return {
          label: status,
          className: 'bg-slate-500 text-white border-0',
          icon: null,
        };
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleStatusChange = (newStatus: 'active' | 'inactive') => {
    setPendingStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleStatusUpdateSuccess = async () => {
    if (patient?.id) {
      await fetchPatient(patient.id);
      if (patient?.id) {
        await fetchSoapNotes(patient.id);
      }
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='flex items-start gap-4'>
          <Skeleton className='h-9 w-9 rounded-lg' />
          <div className='space-y-2'>
            <Skeleton className='h-7 w-40' />
          </div>
        </div>
        <div className='rounded-lg border border-slate-200 bg-white p-5'>
          <div className='flex flex-col gap-5 sm:flex-row sm:items-center'>
            <Skeleton className='h-16 w-16 rounded-xl' />
            <div className='flex-1 space-y-3'>
              <div className='flex items-center gap-3'>
                <Skeleton className='h-6 w-40' />
                <Skeleton className='h-5 w-16 rounded-full' />
              </div>
              <div className='flex gap-4'>
                <Skeleton className='h-4 w-32' />
                <Skeleton className='h-4 w-40' />
              </div>
            </div>
          </div>
        </div>
        <div className='flex gap-6'>
          <div className='w-56 flex-shrink-0'>
            <div className='space-y-2 rounded-lg border border-slate-200 bg-white p-3'>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className='h-12 w-full rounded-md' />
              ))}
            </div>
          </div>
          <div className='min-w-0 flex-1'>
            <Skeleton className='h-32 w-full rounded-lg' />
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (!patient) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='text-center'>
          <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100'>
            <XCircle className='h-6 w-6 text-slate-400' />
          </div>
          <p className='mb-4 text-sm text-slate-600'>Patient not found</p>
          <Button variant='outline' size='sm' onClick={() => navigate('/')} className='text-sm'>
            <ArrowLeft className='mr-2 h-4 w-4' />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(patient.status);

  return (
    <div className='space-y-4'>
      {/* Compact Page Header (no redundant subtitle — breadcrumb already shows patient name) */}
      <div className='flex items-start gap-3'>
        <Button
          variant='outline'
          size='icon'
          onClick={handleBack}
          className='h-9 w-9 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          style={{ marginTop: '4px' }}
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>

        {/* Patient identity — collapsed banner */}
        <div className='flex flex-1 items-center gap-4 rounded-lg border border-slate-200 bg-white p-3'>
          <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-teal-100'>
            <span className='text-base font-semibold text-teal-700'>
              {getInitials(patient.full_name)}
            </span>
          </div>

          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <h1 className='text-lg font-semibold text-slate-900'>
                {toTitleCase(patient.full_name)}
              </h1>
              <Badge className={`${statusConfig.className} text-[11px] font-medium`}>
                <span className='flex items-center gap-1'>
                  {statusConfig.icon}
                  {statusConfig.label}
                </span>
              </Badge>
            </div>
            <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500'>
              {patient.dob && (
                <span className='flex items-center gap-1 text-slate-600'>
                  <Calendar className='h-3 w-3 text-slate-400' />
                  {formatDOB(patient.dob)}
                  {calculateAge(patient.dob) !== null && (
                    <span className='text-slate-400'>
                      ({calculateAge(patient.dob)} yrs)
                    </span>
                  )}
                </span>
              )}
              {defaultEmail && (
                <span className='flex items-center gap-1 text-slate-600'>
                  <Mail className='h-3 w-3 text-slate-400' />
                  {defaultEmail}
                </span>
              )}
              {defaultPhone && (
                <span className='flex items-center gap-1 text-slate-600'>
                  <Phone className='h-3 w-3 text-slate-400' />
                  {defaultPhone}
                </span>
              )}
            </div>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            {!isGuidedClinic &&
              permissionCheck(
                'clinical_notes:create',
                rolePermissions,
                isPractitioner || isAdmin,
              ) && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setIsNoteDrawerOpen(true)}
                  className='border-slate-200'
                >
                  <Plus className='h-4 w-4' />
                  SOAP
                </Button>
              )}
            <Button
              variant='outline'
              size='sm'
              onClick={() => setIsPortalSheetOpen(true)}
              className='border-slate-200 text-slate-600 hover:bg-slate-50'
            >
              <Globe className='h-4 w-4' />
              Portal
            </Button>
            {!isGuidedClinic &&
              (isPractitioner || isAdmin) && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setIsAppointmentSheetOpen(true)}
                  className='border-teal-200 text-teal-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'
                >
                  <Calendar className='h-4 w-4' />
                  Schedule
                </Button>
              )}
            {clinic?.paymentAccount &&
              !isGuidedClinic &&
              permissionCheck(
                'prescriptions:create',
                rolePermissions,
                isPractitioner || isAdmin,
              ) && (
                <Button
                  size='sm'
                  onClick={() => {
                    if (isExternalPatient) {
                      setIsGuidedPickerOpen(true);
                    } else {
                      setIsPrescriptionDrawerOpen(true);
                    }
                  }}
                  className='bg-[#4A8A9A] hover:bg-[#008f87]'
                >
                  <Plus className='h-4 w-4' />
                  Order
                </Button>
              )}
            {permissionCheck(
              'patients:update',
              rolePermissions,
              isPractitioner || isAdmin,
            ) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='outline'
                    size='sm'
                    className='border-slate-200'
                  >
                    <MoreVertical className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem onClick={() => setIsEditDrawerOpen(true)}>
                    <Edit className='mr-2 h-4 w-4' />
                    Edit Patient
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setIsTimelineSheetOpen(true)}>
                    <Clock className='mr-2 h-4 w-4' />
                    Activity Log
                  </DropdownMenuItem>
                  {patient.status === 'active' && (
                    <DropdownMenuItem
                      onClick={() => handleStatusChange('inactive')}
                    >
                      <XCircle className='mr-2 h-4 w-4' />
                      Deactivate
                    </DropdownMenuItem>
                  )}
                  {patient.status === 'inactive' && (
                    <DropdownMenuItem
                      onClick={() => handleStatusChange('active')}
                    >
                      <CheckCircle className='mr-2 h-4 w-4' />
                      Activate
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Payment Account Alert */}
      {!clinic?.paymentAccount && isAdmin && (
        <div className='relative w-full rounded-lg border border-amber-200 bg-amber-50 p-3'>
          <div className='flex items-center space-x-2'>
            <AlertTriangle className='h-4 w-4 text-amber-600' />
            <div className='flex-1 text-sm text-amber-800'>
              <div className='flex items-center justify-between'>
                <span>
                  Your clinic has not linked a payment account. Link a payment
                  account to process payments.
                </span>
              </div>
            </div>
            <Button size='sm' className='ml-4' onClick={() => navigate('/payments?tab=settings')}>
              Go to Payments
            </Button>
          </div>
        </div>
      )}

      {patient?.id && patient?.allergies_need_reconciliation && clinic?.id && (
        <div className='flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
          <div className='flex items-start gap-2'>
            <ShieldAlert className='mt-0.5 h-4 w-4 shrink-0' />
            <div>
              <div className='font-semibold'>Allergies need reconciliation</div>
              <div className='text-xs'>
                Some entries were imported from legacy data and are unconfirmed.
                Review and confirm or remove each one.
              </div>
            </div>
          </div>
          <Button
            size='sm'
            variant='outline'
            className='border-amber-300 bg-white'
            onClick={() => setShowReconcileAllergies(true)}
          >
            Reconcile now
          </Button>
        </div>
      )}

      {patient?.id && clinic?.id && (
        <ReconcileAllergiesDialog
          open={showReconcileAllergies}
          onOpenChange={setShowReconcileAllergies}
          clinicId={clinic.id}
          patientId={patient.id}
        />
      )}

      {/* Secondary nav — lower-frequency surfaces, always shown */}
      <PatientSecondaryNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        showAppointments={!isGuidedClinic}
        showTasks={canViewTasksTab}
        showIntakes={showIntakesTab}
        showInvoices
        showMessaging
        showDocuments
        showMdiCases={isMdiEnabled}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className='flex gap-6'>
          <PatientDetailSidebar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isGuidedClinic={isGuidedClinic}
            isHybridClinic={isHybridClinic}
            isExternalPatient={isExternalPatient}
            rolePermissions={rolePermissions}
            isPractitioner={isPractitioner}
            isAdmin={isAdmin}
          />

          <div className='min-w-0 flex-1'>
            <TabsContent value='summary' className='mt-0 focus-visible:outline-none'>
              <PatientSummaryTab
                patient={patient}
                onPatientUpdated={() => id && fetchPatient(id)}
              />
            </TabsContent>

            <TabsContent value='profile' className='mt-0 focus-visible:outline-none'>
              <PatientProfileTab
                patient={patient}
                onEdit={() => setIsEditDrawerOpen(true)}
              />
            </TabsContent>

            {patient?.id && !isGuidedClinic && (
              <TabsContent value='chart' className='mt-0 focus-visible:outline-none'>
                <PatientChartTab
                  patientId={patient.id}
                  allergiesNeedReconciliation={
                    patient.allergies_need_reconciliation
                  }
                  initialAnchor={chartAnchor}
                />
              </TabsContent>
            )}

            <TabsContent value='orders-rx' className='mt-0 focus-visible:outline-none'>
              {patient?.id && <PatientOrdersRxTab patientId={patient.id} />}
            </TabsContent>

            <TabsContent value='labs' className='mt-0 focus-visible:outline-none'>
              <PatientLabsTab
                patientId={patient?.id}
                patientName={patient?.full_name || ''}
                patientEmail={
                  patient?.email || getDefaultContactValue(patient?.contacts, 'email')
                }
                patientPhone={
                  patient?.phone || getDefaultContactValue(patient?.contacts, 'phone')
                }
                patientAddress={{
                  street: patient?.addresses?.[0]?.line_1,
                  city: patient?.addresses?.[0]?.city,
                  state: patient?.addresses?.[0]?.stateInfo?.code,
                  zip: patient?.addresses?.[0]?.postal_code,
                }}
                patientDob={patient?.dob}
                patientGender={(() => {
                  const rawGender = (patient?.gender || patient?.sex || '').toLowerCase();
                  if (rawGender.startsWith('m')) return 'male';
                  if (rawGender.startsWith('f')) return 'female';
                  return 'unknown';
                })()}
                onPatientUpdated={() => id && refreshPatient(id)}
              />
            </TabsContent>

            {!isGuidedClinic && patient?.id && (
              <TabsContent value='notes' className='mt-0 focus-visible:outline-none'>
                <PatientNotesTab
                  patientId={patient.id}
                  isLoading={isLoadingSoapNotes}
                  notes={recentSoapNotes}
                  pagination={soapNoteParams as PaginationParams}
                  onPaginationChange={(page) =>
                    setSoapNoteParams((prev) => ({
                      ...prev,
                      page: page as number,
                    }))
                  }
                  refetch={async () => {
                    await fetchSoapNotes(patient.id);
                  }}
                />
              </TabsContent>
            )}

            {(isGuidedClinic || isHybridClinic || isExternalPatient) && (
              <TabsContent value='guided-orders' className='mt-0 focus-visible:outline-none'>
                <PatientGuidedOrdersTab
                  patientId={patient?.id}
                  clinicId={isExternalPatient ? patient?.clinic_id : clinic?.id}
                  fulfillmentSource={isExternalPatient ? 'nimbus' : undefined}
                  patient={patient}
                />
              </TabsContent>
            )}

            {/* ── Secondary tabs ── */}

            {!isGuidedClinic && (
              <TabsContent value='appointments' className='mt-0 focus-visible:outline-none'>
                <PatientAppointmentsTab patientId={patient?.id} />
              </TabsContent>
            )}

            {canViewTasksTab && (
              <TabsContent value='tasks' className='mt-0 focus-visible:outline-none'>
                <TaskPanel
                  providerId={user?.practitionerId || ''}
                  patientId={patient?.id}
                />
              </TabsContent>
            )}

            {showIntakesTab && (
              <TabsContent value='intakes' className='mt-0 focus-visible:outline-none'>
                <PatientIntakesTab
                  patientId={patient?.id}
                  patientDbId={patient?.id}
                  patientEmail={
                    patient?.email || getDefaultContactValue(patient?.contacts, 'email')
                  }
                  patientName={patient?.full_name || ''}
                  patientGender={patient?.gender || patient?.sex}
                  mode='intakes'
                />
              </TabsContent>
            )}

            <TabsContent value='documents' className='mt-0 focus-visible:outline-none'>
              <PatientDocumentsTab patientId={patient?.id} />
            </TabsContent>

            <TabsContent
              value='messaging'
              forceMount
              className={cn(
                'mt-0 focus-visible:outline-none',
                activeTab !== 'messaging' && 'hidden',
              )}
            >
              <PatientMessagingTab
                patientId={patient?.id}
                patientName={patient?.full_name || ''}
                patientEmail={
                  patient?.email || getDefaultContactValue(patient?.contacts, 'email')
                }
                patientPhone={
                  patient?.phone || getDefaultContactValue(patient?.contacts, 'phone')
                }
                isActive={activeTab === 'messaging'}
              />
            </TabsContent>

            <TabsContent value='invoices' className='mt-0 focus-visible:outline-none'>
              <PatientInvoicesTab patient={patient} />
            </TabsContent>

            <TabsContent value='shipments' className='mt-0 focus-visible:outline-none'>
              <PatientShipmentsTab
                shipments={recentShipments}
                onViewAll={() => setShipmentsSheetOpen(true)}
              />
            </TabsContent>

{isMdiEnabled && (
              <TabsContent value='mdi-cases' className='mt-0 focus-visible:outline-none'>
                {patient?.id && (
                  <PatientMdiCasesTab
                    patientId={patient.id}
                    mdiAuthLink={patient.mdi_auth_link}
                  />
                )}
              </TabsContent>
            )}
          </div>

        </div>
      </Tabs>

      {/* Modals and Sheets */}
      {isEditDrawerOpen && (
        <PatientFormDrawer
          open={isEditDrawerOpen}
          onOpenChange={setIsEditDrawerOpen}
          patient={patient as never}
          onSubmit={handleEditPatient}
          assignedPractitioner={patient?.practitioner_id}
          assignedClinic={patient?.clinic_id}
        />
      )}

      {isGuidedPickerOpen && patient?.id && patient?.clinic_id && user?.practitionerId && (
        <GuidedOrderPickerDialog
          open={isGuidedPickerOpen}
          onOpenChange={setIsGuidedPickerOpen}
          patientId={patient.id}
          patientClinicId={patient.clinic_id}
          myPractitionerId={user.practitionerId}
          onSelect={(orderId) => {
            setPendingGuidedOrderId(orderId);
            setIsGuidedPickerOpen(false);
            setIsPrescriptionDrawerOpen(true);
          }}
          onViewAll={() => setActiveTab('guided-orders')}
        />
      )}

      {isPrescriptionDrawerOpen && (
        <PrescriptionFormSheet
          open={isPrescriptionDrawerOpen}
          onOpenChange={(next) => {
            setIsPrescriptionDrawerOpen(next);
            if (!next) setPendingGuidedOrderId(null);
          }}
          patientId={patient?.id}
          practitionerId={user?.practitionerId}
          mode='create'
          onSubmit={onSuccess}
          guidedOrderId={pendingGuidedOrderId ?? undefined}
        />
      )}

      {isNoteDrawerOpen && patient?.id && (
        <NoteFormDrawer
          open={isNoteDrawerOpen}
          onOpenChange={setIsNoteDrawerOpen}
          patientId={patient.id}
          onSubmit={handleCreateNote}
          note={null}
        />
      )}

      {soapNotesSheetOpen && patient?.id && (
        <SOAPNotesListSheet
          open={soapNotesSheetOpen}
          onOpenChange={setSoapNotesSheetOpen}
          patientId={patient.id}
        />
      )}

      {statusDialogOpen && patient && (
        <UpdatePatientStatusDialog
          open={statusDialogOpen}
          onOpenChange={setStatusDialogOpen}
          patient={patient}
          newStatus={pendingStatus}
          onSuccess={handleStatusUpdateSuccess}
        />
      )}

      {isAppointmentSheetOpen && clinic?.id && patient?.id && (
        <AppointmentWizard
          open={isAppointmentSheetOpen}
          onOpenChange={setIsAppointmentSheetOpen}
          clinicId={clinic.id}
          defaultPatientId={patient.id}
          onSave={() => {
            setIsAppointmentSheetOpen(false);
            queryClient.invalidateQueries({
              queryKey: patientAppointmentsQueryKey(clinic.id, patient.id),
            });
            toast({
              title: 'Success',
              description: 'Appointment scheduled successfully',
              variant: 'success',
            });
          }}
        />
      )}

      {/* Activity Log — opened from the ⋮ dropdown. Renders the existing
          PatientTimelineTab content (audit trail of clinical events,
          appointments, Rx, labs, status changes). Lives in the kebab menu
          rather than the secondary nav because it's audit / historical
          reference, not a working surface. */}
      <Sheet
        open={isTimelineSheetOpen}
        onOpenChange={setIsTimelineSheetOpen}
      >
        <SheetContent
          side='right'
          className='flex w-full !max-w-2xl flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'
        >
          {/* Header */}
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <SheetTitle
              className='flex items-center gap-2 font-semibold text-slate-900'
              style={{ fontSize: '1.05rem' }}
            >
              <Clock className='h-5 w-5 text-slate-600' />
              Activity Log
            </SheetTitle>
            <SheetClose asChild>
              <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
                <X className='h-5 w-5 text-slate-600' />
              </button>
            </SheetClose>
          </div>

          {/* Body */}
          <div className='flex-1 space-y-4 overflow-y-auto px-6 py-5'>
            <SheetDescription className='text-sm text-slate-500'>
              Chronological record of clinical events, status changes, and audit
              entries for this patient.
            </SheetDescription>
            {patient?.id && <PatientTimelineTab patientId={patient.id} />}
          </div>
        </SheetContent>
      </Sheet>

      {/* Patient Portal — header pill opens this sheet. Lives outside the
          Summary tab so it's reachable from any tab and matches Athena /
          Elation's portal-status header pattern. */}
      <Sheet open={isPortalSheetOpen} onOpenChange={setIsPortalSheetOpen}>
        <SheetContent
          side='right'
          className='flex w-full !max-w-xl flex-col gap-0 rounded-l-2xl p-0 [&>button]:hidden'
        >
          {/* Header */}
          <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
            <SheetTitle
              className='flex items-center gap-2 font-semibold text-slate-900'
              style={{ fontSize: '1.05rem' }}
            >
              <Globe className='h-5 w-5 text-slate-600' />
              Patient Portal
            </SheetTitle>
            <SheetClose asChild>
              <button className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-50'>
                <X className='h-5 w-5 text-slate-600' />
              </button>
            </SheetClose>
          </div>

          {/* Body */}
          <div className='flex-1 space-y-4 overflow-y-auto px-6 py-5'>
            <SheetDescription className='text-sm text-slate-500'>
              Send invites, manage active portal sessions, and revoke access.
            </SheetDescription>
            {patient?.id && (
              <PatientPortalSection
                patientId={patient.id}
                patientEmail={defaultEmail || undefined}
                patientPhone={defaultPhone || undefined}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default PatientDetail;
